import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader, AlertCircle, CheckCircle2 } from 'lucide-react';
import SignatureCapture from '../components/SignatureCapture';
import LivePhotoCapture from '../components/LivePhotoCapture';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.orello.in/api';

/**
 * WitnessSigning
 *
 * Public, no-account page for a witness to preview an agreement and sign it.
 * Reached via a single-use emailed link: /agreements/witness-sign/:token
 * The token itself is the credential - there is no login, no OTP, nothing else
 * to verify. Signature is mandatory; live photo is optional for witnesses.
 */
export default function WitnessSigning() {
  const { token } = useParams();

  const [loadState, setLoadState] = useState('loading'); // loading | ready | invalid | already_done
  const [invite, setInvite] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  const [signatureData, setSignatureData] = useState(null);
  const [signatureUrl, setSignatureUrl] = useState(null);
  const [photoImage, setPhotoImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/agreements/witness-sign/${token}`);
        const data = await res.json();
        if (!res.ok) {
          setErrorMsg(data.detail || 'This witness link is invalid.');
          setLoadState('invalid');
          return;
        }
        setInvite(data);
        setLoadState(data.already_signed || data.used ? 'already_done' : 'ready');
      } catch (err) {
        console.error('Failed to load witness invite:', err);
        setErrorMsg('Could not load this witness link. Please check your connection and try again.');
        setLoadState('invalid');
      }
    })();
  }, [token]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch(`${API_URL}/agreements/witness-sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature_image: signatureUrl,
          photo_image: photoImage,
          name_typed: signatureData?.name || invite?.witness_name,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to submit your signature.');
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const containerStyle = {
    minHeight: '100vh',
    background: '#0a0a0a',
    color: '#f5f5f5',
    padding: '2rem 1rem',
  };
  const cardStyle = {
    maxWidth: '640px',
    margin: '0 auto',
  };

  if (loadState === 'loading') {
    return (
      <div style={containerStyle}>
        <div style={{ ...cardStyle, display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: '4rem' }}>
          <Loader className="w-8 h-8 animate-spin" style={{ color: '#F97316' }} />
          <p style={{ marginTop: '1rem', color: '#a3a3a3' }}>Loading agreement...</p>
        </div>
      </div>
    );
  }

  if (loadState === 'invalid') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-6 flex gap-3">
            <AlertCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Link Unavailable</h2>
              <p style={{ color: '#fca5a5', fontSize: '0.9rem' }}>{errorMsg}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadState === 'already_done' && !submitted) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-6 flex gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Already Signed</h2>
              <p style={{ color: '#a7f3d0', fontSize: '0.9rem' }}>
                This witness signature has already been submitted. Thank you.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div className="bg-emerald-900/30 border border-emerald-700 rounded-lg p-6 flex gap-3">
            <CheckCircle2 className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-0.5" />
            <div>
              <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '0.5rem' }}>Thank You</h2>
              <p style={{ color: '#a7f3d0', fontSize: '0.9rem' }}>
                Your witness signature has been recorded on the agreement. You can close this page now.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Ready to sign
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Witness Signature</h1>
          <p style={{ color: '#a3a3a3', fontSize: '0.9rem' }}>
            {invite.licensor_name && invite.licensee_name
              ? `You are witnessing a rental agreement between ${invite.licensor_name} and ${invite.licensee_name}.`
              : 'You are witnessing a rental agreement.'}
            {invite.property_address && <> Property: {invite.property_address}.</>}
          </p>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded-lg p-4 mb-6">
          <p style={{ fontSize: '0.85rem', color: '#a3a3a3', marginBottom: '0.75rem' }}>Agreement Preview</p>
          <iframe
            src={`${API_URL}/agreements/witness-sign/${token}/pdf`}
            title="Agreement Preview"
            style={{ width: '100%', height: '400px', border: 'none', borderRadius: '4px', background: '#fff' }}
          />
        </div>

        <div className="space-y-6">
          <SignatureCapture
            initialName={invite.witness_name}
            onSignatureCapture={(sig) => {
              setSignatureData(sig);
              if (sig.method === 'draw' || sig.method === 'upload') {
                setSignatureUrl(sig.image);
              }
            }}
          />

          {signatureUrl && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-3">✓ Signature Preview</p>
              <div className="bg-white rounded p-4 text-center">
                <img src={signatureUrl} alt="Your Signature" className="max-h-24 mx-auto object-contain" />
              </div>
            </div>
          )}

          <LivePhotoCapture
            label="Live Photo (optional)"
            onCapture={setPhotoImage}
            onClear={() => setPhotoImage(null)}
          />

          {submitError && (
            <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 flex gap-3">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-200">{submitError}</p>
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !signatureData}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg font-medium transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting && <Loader className="w-4 h-4 animate-spin" />}
            {isSubmitting ? 'Submitting...' : '✓ Confirm & Sign as Witness'}
          </button>
        </div>
      </div>
    </div>
  );
}
